// src/modules/panelGlobal/components/ReporteEquiposSinFacturarDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Tabs, Tab,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, CircularProgress, Alert, Typography, Stack, IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import { getReporteEquiposSinFacturar } from "../services/panelGlobalService";
import BusquedaPorIdentificador from "../../centroEquipos/components/BusquedaPorIdentificador";

const headCellSx = {
  fontWeight: 800,
  py: 0.5,
  fontSize: "0.72rem",
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};

const bodyCellSx = { py: 0.4, fontSize: "0.8rem" };

const formatFecha = (iso) => {
  if (!iso) return "Nunca";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Nunca";
  return d.toLocaleDateString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatFechaHora = (iso) => {
  if (!iso) return "Sin datos";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Sin datos";
  return d.toLocaleString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const filtrarFilas = (filas, campos, buscar) => {
  const texto = buscar.trim().toUpperCase();
  if (!texto) return filas;
  return filas.filter((f) => campos.some((c) => String(f[c] || "").toUpperCase().includes(texto)));
};

function TablaConPaginacion({ filas, columnas, exportar, vacioMensaje, onRowClick }) {
  const [buscar, setBuscar] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const camposBuscables = useMemo(() => columnas.map((c) => c.field), [columnas]);
  const filasFiltradas = useMemo(
    () => filtrarFilas(filas, camposBuscables, buscar),
    [filas, camposBuscables, buscar]
  );
  const filasPagina = useMemo(() => {
    const start = page * rowsPerPage;
    return filasFiltradas.slice(start, start + rowsPerPage);
  }, [filasFiltradas, page, rowsPerPage]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          label="Buscar…"
          value={buscar}
          onChange={(e) => { setBuscar(e.target.value); setPage(0); }}
          sx={{ minWidth: 220 }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {filasFiltradas.length} equipo(s)
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon fontSize="small" />}
            disabled={!filasFiltradas.length}
            onClick={() => exportar(filasFiltradas)}
          >
            Exportar Excel
          </Button>
        </Stack>
      </Stack>

      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columnas.map((c) => (
                <TableCell key={c.field} sx={headCellSx}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!filasPagina.length ? (
              <TableRow>
                <TableCell colSpan={columnas.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center", py: 2 }}>
                    {vacioMensaje}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filasPagina.map((fila, i) => (
                <TableRow
                  key={`${fila.num_sim}-${i}`}
                  hover
                  onClick={() => onRowClick?.(fila)}
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columnas.map((c) => (
                    <TableCell key={c.field} sx={bodyCellSx}>
                      {c.render ? c.render(fila) : fila[c.field] || "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filasFiltradas.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
      />
    </Box>
  );
}

const columnaUltimoReporte = (labelPlataforma) => ({
  field: "ultimo_reporte_plataforma",
  label: `Última Actualización ${labelPlataforma}`,
  render: (f) => (
    <Chip
      size="small"
      variant="outlined"
      color={f.ultimo_reporte_plataforma ? "default" : "error"}
      label={formatFechaHora(f.ultimo_reporte_plataforma)}
    />
  ),
  exportValue: (f) => formatFechaHora(f.ultimo_reporte_plataforma),
});

const columnasSinRelacion = (labelPlataforma) => [
  { field: "num_sim", label: "Num SIM" },
  { field: "emai", label: "EMAI" },
  { field: "nombre_plataforma", label: `Nombre ${labelPlataforma}` },
  columnaUltimoReporte(labelPlataforma),
  { field: "modelo", label: "Modelo" },
  { field: "placa", label: "Placa" },
  { field: "estado", label: "Estado", render: (f) => <Chip size="small" variant="outlined" label={f.estado} /> },
  { field: "sucursal", label: "Sucursal" },
  { field: "grupo", label: "Grupo" },
];

const columnasNoFacturado = (labelPlataforma) => [
  { field: "num_sim", label: "Num SIM" },
  { field: "emai", label: "EMAI" },
  { field: "nombre_plataforma", label: `Nombre ${labelPlataforma}` },
  columnaUltimoReporte(labelPlataforma),
  { field: "modelo", label: "Modelo" },
  { field: "placa", label: "Placa" },
  { field: "estado", label: "Estado", render: (f) => <Chip size="small" variant="outlined" label={f.estado} /> },
  { field: "cliente", label: "Cliente" },
  { field: "sucursal_facturar", label: "Sucursal" },
  { field: "servicio_descripcion", label: "Servicio" },
  {
    field: "vinculado_desde",
    label: "Vinculado Desde",
    render: (f) => formatFecha(f.vinculado_desde),
    exportValue: (f) => formatFecha(f.vinculado_desde),
  },
  {
    field: "ultima_factura",
    label: "Último Documento",
    render: (f) => (
      <Chip
        size="small"
        variant="outlined"
        color={f.ultima_factura ? "warning" : "error"}
        label={formatFecha(f.ultima_factura)}
      />
    ),
    exportValue: (f) => formatFecha(f.ultima_factura),
  },
];

const exportarExcel = (filas, columnas, nombreArchivo, hoja) => {
  const encabezado = columnas.map((c) => c.label);
  const cuerpo = filas.map((f) => columnas.map((c) => (c.exportValue ? c.exportValue(f) : f[c.field] || "-")));
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, XLSX.utils.aoa_to_sheet([encabezado, ...cuerpo]), hoja);
  XLSX.writeFile(libro, nombreArchivo);
};

export default function ReporteEquiposSinFacturarDialog({ open, onClose, plataforma = "wialon", labelPlataforma = "Wialon" }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ sinRelacion: [], sinFacturar: [], total_unidades: 0 });
  const [modemDetalle, setModemDetalle] = useState(null);

  const columnasSinRelacionActual = useMemo(() => columnasSinRelacion(labelPlataforma), [labelPlataforma]);
  const columnasNoFacturadoActual = useMemo(() => columnasNoFacturado(labelPlataforma), [labelPlataforma]);

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    setLoading(true);
    setError("");
    setData({ sinRelacion: [], sinFacturar: [], total_unidades: 0 });
    getReporteEquiposSinFacturar(3, plataforma)
      .then(({ data: resp }) => {
        if (cancelado) return;
        setData({
          sinRelacion: resp.sinRelacion || [],
          sinFacturar: resp.sinFacturar || [],
          total_unidades: resp.total_unidades || 0,
        });
      })
      .catch((e) => {
        if (cancelado) return;
        setError(e?.response?.data?.error || "No se pudo generar el reporte.");
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => { cancelado = true; };
  }, [open, plataforma]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Equipos sin Facturar — {labelPlataforma}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Módems registrados como unidad en {labelPlataforma} ahora mismo
          {data.total_unidades ? ` (${data.total_unidades} unidades)` : ""} — ahí es donde se
          paga la suscripción — cruzados contra relación de cliente y facturación:
          sin ninguna relación vigente con un cliente, o relacionados pero sin
          factura o prefactura emitida en los últimos 3 meses (se excluyen los
          vinculados a su cliente actual hace menos de 3 meses — ahí "nunca
          facturado" es esperable, no una alerta real).
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
          <Tab label={`Sin relación con cliente (${data.sinRelacion.length})`} />
          <Tab label={`No facturado 3+ meses (${data.sinFacturar.length})`} />
        </Tabs>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : tab === 0 ? (
          <TablaConPaginacion
            filas={data.sinRelacion}
            columnas={columnasSinRelacionActual}
            vacioMensaje="No hay módems sin relación con un cliente."
            exportar={(filas) =>
              exportarExcel(filas, columnasSinRelacionActual, `equipos_sin_relacion_${plataforma}.xlsx`, "Sin relación")
            }
            onRowClick={(fila) => setModemDetalle(fila.num_sim)}
          />
        ) : (
          <TablaConPaginacion
            filas={data.sinFacturar}
            columnas={columnasNoFacturadoActual}
            vacioMensaje="No hay módems relacionados sin facturar hace 3+ meses."
            exportar={(filas) =>
              exportarExcel(filas, columnasNoFacturadoActual, `equipos_no_facturados_${plataforma}.xlsx`, "No facturado")
            }
            onRowClick={(fila) => setModemDetalle(fila.num_sim)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      <Dialog
        open={Boolean(modemDetalle)}
        onClose={() => setModemDetalle(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ py: 0.75, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          Módem {modemDetalle}
          <IconButton
            size="small"
            onClick={() => setModemDetalle(null)}
            sx={{ position: "absolute", right: 8, top: 5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0.75 }}>
          {modemDetalle && (
            <BusquedaPorIdentificador
              initialTipo="modem"
              initialBuscar={modemDetalle}
              autoLoadKey={modemDetalle}
              hideSearch
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
